import { Prose } from '../../components/Prose'
import { useEffect, useRef, useState } from 'react'
import { useDeleteSession, useLastLogs, useSaveSession, useSchedule, useSessions } from '../../api/hooks'
import { todayIso } from '../../lib/format'
import { nextWorkout } from '../../lib/schedule'
import {
  generateProgram, restFor, splitOf,
  type CatalogExercise, type ProgramEdits,
} from '../../lib/program'
import { ExerciseImage } from '../../components/ExerciseImage'
import { useRest } from './RestTimerProvider'
import { ExerciseHistory } from './ExerciseHistory'
import { personalRecords, sessionStats, suggestProgression } from '../../lib/training'
import { muscleTr } from '../../lib/program'
import { bantBasamaklari } from '../../lib/bant'
import { formatRest } from './useRestTimer'
import {
  RPE_SCALE, SET_KIND_LABEL, SET_KIND_SHORT, addSet, canFinish, draftFromDay, exerciseDone,
  isLoadable, missingRpe, nextKind, prevSetText, progress, removeSet, removeSetGuard,
  rpeVerdict, setRpe, setSetKind, shortDate, targetText, toUpsert, toggleSet, toggleSkip, updateSet,
  type ExerciseLog, type Session, type SetKind,
} from '../../lib/session'
import { api } from '../../api/client'

interface Props {
  catalog: CatalogExercise[]
  equipment: string[]
  /** Elindeki direnç bantları (kg). Bant hareketlerinde kg kutusunu ve
      ilerleme basamaklarını bu açıyor — bkz. lib/bant.ts. */
  bandsKg: number[]
  /** Kullanıcının elle seçtiği hareketler — program burada da aynı olsun. */
  picks: Record<string, string>
  /**
   * Gün bazlı düzenlemeler. Bugünün antrenmanı Program sekmesinde görünenle
   * birebir aynı olmalı; iki yerde iki farklı liste çıkarsa hangisinin doğru
   * olduğu belirsizleşir.
   */
  edits: ProgramEdits
  /** Seçilen bölünme — kaç şablon olduğunu ve rotasyonu belirliyor. */
  split?: string | null
  /**
   * Hangi günün antrenmanı. Verilmezse bugün.
   *
   * Geriye dönük giriş için var: "pazartesi yaptım ama yazmayı unuttum" gerçek
   * bir durum ve kaydı ancak o güne yazarsan ilerleme grafikleri, haftalık
   * kota ve "geçen sefer ne yaptın" satırı doğru çalışıyor. Bugüne yazmak
   * veriyi kurtarmaz, bozar.
   */
  date?: string
  /** Geçmiş güne yazarken üstte uyarı gösteriliyor. */
  onClose?: () => void
}

export function SessionCard({ catalog, equipment, bandsKg, picks, edits, split, date, onClose }: Props) {
  const today = date ?? todayIso()
  const gercekBugun = todayIso()
  const gecmisGun = today !== gercekBugun
  const sessions = useSessions()
  const last = useLastLogs()
  const sched = useSchedule()
  const save = useSaveSession()
  const del = useDeleteSession()
  const rest = useRest()

  const server = sessions.data?.find((s) => s.date === today) ?? null
  const [draft, setDraft] = useState<Session | null>(null)
  const hydrated = useRef(false)
  const dirty = useRef(false)

  // Sunucudaki kaydı bir kez içeri al. Sonrasında yerel taslak doğruyu tutuyor —
  // her kaydetmeden sonra gelen refetch yazdıklarımızı geri almasın.
  useEffect(() => {
    if (hydrated.current || sessions.isLoading) return
    hydrated.current = true
    setDraft(server)
  }, [sessions.isLoading, server])

  // Otomatik kayıt. Her tuşta istek atmamak için 700 ms bekliyor;
  // "Kaydet" düğmesi yok, olması gereken bir şey de değil.
  useEffect(() => {
    if (!draft || !dirty.current) return
    const t = setTimeout(() => { dirty.current = false; save.mutate(draft) }, 700)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft])

  /**
   * Arka plana giderken bekleyen kaydı hemen yolla.
   *
   * 700 ms'lik bekleme masaüstünde zararsız ama telefonda gerçek bir kayıp
   * penceresi: son seti işaretleyip ekranı kilitleyen biri o süreyi doldurmadan
   * gidiyor, iOS sayfayı donduruyor ve set kayboluyor. `visibilitychange` iOS'ta
   * güvenilir şekilde ateşleniyor (`beforeunload` etmiyor), `pagehide` de sekme
   * kapanışını yakalıyor.
   */
  const draftRef = useRef<Session | null>(null)
  draftRef.current = draft
  useEffect(() => {
    const flush = () => {
      if (!dirty.current || !draftRef.current) return
      dirty.current = false
      api.saveSessionBeacon(today, toUpsert(draftRef.current))
    }
    const onVis = () => { if (document.visibilityState === 'hidden') flush() }
    document.addEventListener('visibilitychange', onVis)
    window.addEventListener('pagehide', flush)
    return () => {
      document.removeEventListener('visibilitychange', onVis)
      window.removeEventListener('pagehide', flush)
    }
  }, [today])

  const edit = (fn: (s: Session) => Session) => {
    dirty.current = true
    setDraft((s) => (s ? fn(s) : s))
  }

  /**
   * Set işaretlenince dinlenme sayacı kendiliğinden başlıyor.
   * Sadece set AÇILIRKEN — işareti geri alırken değil (yanlışlıkla
   * işaretleyip düzelten biri için sayaç başlatmak gürültü olur).
   */
  const markSet = (exIndex: number, setIndex: number) => {
    const e = draft?.exercises[exIndex]
    const willBeDone = e ? !e.sets[setIndex].done : false
    edit((s) => toggleSet(s, exIndex, setIndex))
    if (willBeDone && e) rest.start(restFor(e.pattern), e.name)
    else if (!willBeDone) rest.stop()
  }

  /** Beklemeye değmeyecek işlemler (başlat/bitir) anında yazılır. */
  const commit = (next: Session) => {
    dirty.current = false
    setDraft(next)
    save.mutate(next)
  }

  const byslug = new Map(catalog.map((x) => [x.slug, x]))

  // Harekete özel geçmiş — çift ilerleme kararı için son iki seans lazım,
  // "geçen sefer" satırındaki tek kayıt yetmiyor (2-for-2 kuralı).
  const history = new Map<string, ExerciseLog[]>()
  for (const s of (sessions.data ?? []).filter((x) => x.completedAt !== null && x.date < today))
    for (const e of s.exercises)
      history.set(e.slug, [...(history.get(e.slug) ?? []), e])

  // Seçilen günü saymıyoruz: kullanıcı Takip sekmesinden "kuvvet yapıldı"yı önce
  // işaretlediyse sayaç bir ileri kayar ve o güne yanlış harf (B) düşerdi.
  // Geçmiş bir güne yazarken de aynı mantık: o günden ÖNCEKİ antrenmanlar sayılır,
  // yoksa geriye dönük giren biri bugünün sırasını görürdü.
  const before = (sched.data?.strengthDoneDates ?? []).filter((d) => d < today).length
  // Rotasyon şablon sayısına göre: iki şablonda A-B-A, üç şablonda A-B-C-A.
  const def = splitOf(split)
  const label = draft?.day ?? nextWorkout(before, def.days.length)
  const day = generateProgram({ catalog, equipment, picks, edits, split: def.id })
    .days.find((d) => d.label === label)
  // Bant basamakları her hareket için aynı; bir kez hesaplanıp paylaşılıyor.
  const bant = { basamaklar: bantBasamaklari(bandsKg), bandsKg }
  // Kullanıcı güne ad verdiyse onu kullanıyoruz: Program sekmesinde "Bacak Günü"
  // yazıp burada "Antrenman A" demek aynı şeyin iki adı olur.
  const dayName = day?.name ?? `Antrenman ${label}`

  if (sessions.isLoading) {
    return <div className="card"><p className="sub">Antrenman kaydı yükleniyor…</p></div>
  }

  /** Geçmiş güne yazarken üstte duran şerit — hangi tarihe yazdığın belirsiz kalmasın. */
  const gecmisUyari = gecmisGun ? (
    <div className="warnbox" style={{ marginTop: 0, marginBottom: 14 }}>
      <strong>{shortDate(today)}</strong> gününe geriye dönük yazıyorsun.
      {onClose && (
        <button style={{ marginLeft: 10, padding: '3px 10px', fontSize: 12 }} onClick={onClose}>
          Bugüne dön
        </button>
      )}
    </div>
  ) : null

  // ── Henüz başlanmadı ──────────────────────────────────────────────────────
  if (!draft) {
    return (
      <div className="card">
        {gecmisUyari}
        <h2>{gecmisGun ? `${shortDate(today)} antrenmanı` : 'Bugünkü antrenman'}</h2>
        <Prose className="card-h">
          {gecmisGun ? 'O gün için önerilen' : 'Sıradaki'}: <strong>{dayName}</strong>{' '}
          ({day?.exercises.length ?? 0} hareket).
          {gecmisGun
            ? ' Başlat, sonra gerçekten yaptığın setleri ve ağırlıkları gir. Hareketleri değiştirebilirsin.'
            : ' Başlattığında her set için ne yaptığını işaretleyeceksin; bitince kaydedilir ve takip sekmesindeki kuvvet işareti kendiliğinden düşer.'}
        </Prose>
        <button
          className="primary"
          disabled={!day || day.exercises.length === 0}
          onClick={() => day && commit(draftFromDay(
            today, day, last.data ?? {},
            // Program feedback'e göre kendini yeniden kuruyor: yeni yük ve tekrar
            // hedefi doğrudan taslağa yazılıyor, "şunu yapsan iyi olur" notu değil.
            Object.fromEntries(day.exercises.map((p) => {
              const sug = suggestProgression(
                history.get(p.exercise.slug) ?? [],
                isLoadable(p.exercise.equipment, bandsKg), bant)
              return [p.exercise.slug,
                      { nextWeightKg: sug.nextWeightKg, nextReps: sug.nextReps }]
            })),
          ))}
        >
          {dayName} · başlat
        </button>
        {(!day || day.exercises.length === 0) && (
          <Prose className="note">Önce aşağıdan ekipmanını seç — program üretilemedi.</Prose>
        )}
      </div>
    )
  }

  // ── Devam eden / bitmiş antrenman ─────────────────────────────────────────
  const pr = progress(draft)
  const eksik = missingRpe(draft)
  const bitti = draft.completedAt !== null

  return (
    <div className="card">
      {gecmisUyari}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
        <h2 style={{ marginBottom: 0 }}>
          {gecmisGun ? shortDate(today) : 'Bugün'} ·{' '}
          {draft.day === label ? dayName : `Antrenman ${draft.day}`}{bitti ? ' · tamamlandı' : ''}
        </h2>
        <span className="sub" style={{ fontSize: 13 }}>
          {pr.done}/{pr.total} set{save.isPending && ' · kaydediliyor…'}
        </span>
      </div>

      <div className="bar-track" style={{ margin: '10px 0 18px' }}>
        <div className="bar-fill" style={{ width: `${pr.pct}%` }} />
      </div>

      {draft.exercises.map((e, i) => (
        <ExerciseBlock
          key={e.slug + i}
          e={e}
          index={i}
          equipment={byslug.get(e.slug)?.equipment ?? 'bodyweight'}
          bandsKg={bandsKg}
          image={byslug.get(e.slug)?.images[0]}
          prev={last.data?.[e.slug]}
          catalogName={byslug.get(e.slug)?.nameTr}
          readOnly={bitti}
          edit={edit}
          onMarkSet={(k) => markSet(i, k)}
          restSeconds={restFor(e.pattern)}
          suggestion={suggestProgression(
            history.get(e.slug) ?? [],
            isLoadable(byslug.get(e.slug)?.equipment ?? '', bandsKg), bant)}
        />
      ))}

      {!bitti && (
        <>
          {eksik.length > 0 && pr.done > 0 && (
            <Prose className="note" style={{ marginBottom: 10 }}>
              Zorluk girilmemiş: {eksik.join(', ')}. Girmezsen de bitirebilirsin, ama bir
              sonraki antrenmanın yükü buna bakılarak ayarlanacak.
            </Prose>
          )}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              className="primary"
              disabled={!canFinish(draft)}
              onClick={() => {
                // Antrenman bitince dinlenme sayacı da dursun — bitmiş bir
                // antrenmanın ardından geri sayan bir şerit anlamsız.
                rest.stop()
                commit({ ...draft, completedAt: new Date().toISOString() })
              }}
            >
              Antrenmanı bitir
            </button>
            <button onClick={() => {
              rest.stop()
              hydrated.current = true; dirty.current = false; setDraft(null); del.mutate(today)
            }}>
              Kaydı sil
            </button>
          </div>
          {!canFinish(draft) && (
            <Prose className="note">En az bir set işaretlemeden bitiremezsin.</Prose>
          )}
        </>
      )}

      {bitti && <Summary session={draft} catalog={catalog} history={history} />}

      {bitti && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button onClick={() => commit({ ...draft, completedAt: null })}>Yeniden aç</button>
          <button onClick={() => { hydrated.current = true; dirty.current = false; setDraft(null); del.mutate(today) }}>
            Kaydı sil
          </button>
        </div>
      )}
    </div>
  )
}

/**
 * Antrenman bitiş özeti.
 *
 * "Bugün ne yaptım" sorusunun tek ekranlık cevabı. Süslü olması gerekmiyor,
 * DOĞRU olması gerekiyor: ısınma setleri hacme girmiyor, atlanan hareket
 * saklanmıyor, rekor tanımı dar tutuldu (her seansta rozet dağıtan sistem
 * rozetleri değersizleştirir).
 */
function Summary({ session, catalog, history }: {
  session: Session
  catalog: CatalogExercise[]
  history: Map<string, ExerciseLog[]>
}) {
  const st = sessionStats(session, catalog)
  const pr = personalRecords(session, history)
  const top = st.byMuscle.slice(0, 6)
  const max = top[0]?.sets ?? 1

  return (
    <div className="summary">
      <h3 style={{ marginTop: 0 }}>Antrenman özeti</h3>

      <div className="grid g4" style={{ marginBottom: 14 }}>
        <div className="tile">
          <div className="lab">Toplam yük</div>
          <div className="val">{st.volumeKg.toLocaleString('tr-TR')}<span className="hero-u" style={{ fontSize: 14 }}> kg</span></div>
          <div className="dl">tekrar × ağırlık</div>
        </div>
        <div className="tile">
          <div className="lab">İş seti</div>
          <div className="val">{st.workSets}</div>
          <div className="dl">
            {st.warmupSets > 0 ? `+ ${st.warmupSets} ısınma (sayılmadı)` : 'ısınma yok'}
          </div>
        </div>
        <div className="tile">
          <div className="lab">Hareket</div>
          <div className="val">{st.exercises}</div>
          <div className="dl">{st.skipped > 0 ? `${st.skipped} tanesi atlandı` : 'hepsi yapıldı'}</div>
        </div>
      </div>

      {top.length > 0 && (
        <>
          <h3>Bugün ne çalıştı</h3>
          <div className="muscles">
            {top.map((m) => (
              <div className="muscle" key={m.muscle}>
                <span className="muscle-name">{muscleTr(m.muscle)}</span>
                <span className="muscle-bar">
                  <span style={{ width: `${(m.sets / max) * 100}%` }} />
                </span>
                <span className="muscle-val">{Math.round(m.sets * 10) / 10}</span>
              </div>
            ))}
          </div>
          <Prose className="note">
            Fraksiyonel set: birincil kas 1, ikincil 0.5 sayılıyor. Haftalık toplamı
            ve hedefe göre yerini "Haftalık yük" bölümünde görebilirsin.
          </Prose>
        </>
      )}

      {pr.length > 0 && (
        <>
          <h3>Rekorlar</h3>
          {pr.map((r) => (
            <div className="prline" key={r.slug + r.kind}>
              <span className="pill pill-good">
                {r.kind === 'weight' ? 'yeni ağırlık' : r.kind === 'first' ? 'ilk kayıt'
                  : r.kind === 'hold' ? 'daha uzun' : 'daha çok tekrar'}
              </span>
              <strong>{r.name}</strong>
              <span className="sub" style={{ fontSize: 13 }}>{r.message}</span>
            </div>
          ))}
        </>
      )}
    </div>
  )
}

function ExerciseBlock({ e, index, equipment, bandsKg, image, prev, catalogName, readOnly, edit, onMarkSet, restSeconds, suggestion }: {
  e: ExerciseLog
  index: number
  equipment: string
  bandsKg: number[]
  image?: string
  prev?: { date: string; log: ExerciseLog }
  catalogName?: string
  readOnly: boolean
  edit: (fn: (s: Session) => Session) => void
  onMarkSet: (setIndex: number) => void
  restSeconds: number
  suggestion: ReturnType<typeof suggestProgression>
}) {
  const [showHistory, setShowHistory] = useState(false)
  const loadable = isLoadable(equipment, bandsKg)
  const done = exerciseDone(e)
  const verdict = rpeVerdict(e.rpe)

  return (
    <div className="day" style={{ marginBottom: 12, opacity: e.skipped ? 0.55 : 1 }}>
      {/* Telefonda üç sütun (görsel · ad · iki düğme) sığmıyordu: 390px ekranda
          ada ~140px kalıyor ve "3 × 11–12 tekrar · 1:30 dinlenme · son: 2 Ağu"
          dört satıra bölünüyordu. Düzen CSS'te ızgaraya çevrildi; düğmeler dar
          ekranda kendi satırına iniyor. */}
      <div className="exhead">
        <ExerciseImage images={image ? [image] : []} pattern={e.pattern}
                       className="exthumb sm" width={64} height={48} />
        <div className="exhead-main">
          <div className="dt">{done && !e.skipped ? '✓ ' : ''}{e.name}</div>
          {catalogName && catalogName !== e.name && (
            <div className="dd" style={{ color: 'var(--muted)' }}>{catalogName}</div>
          )}
          <div className="dd">
            {targetText(e)} · {formatRest(restSeconds)} dinlenme
            {prev && <> · son: {shortDate(prev.date)}</>}
          </div>
        </div>
        <div className="exhead-actions">
          {/* Geçmiş burada, ayrı bir ekranda değil: "geçen ay ne kaldırmıştım"
              sorusu tam bu hareketi yaparken soruluyor. */}
          <button style={{ padding: '3px 9px', fontSize: 12 }}
                  aria-expanded={showHistory}
                  onClick={() => setShowHistory(!showHistory)}>
            {showHistory ? 'Kapat' : 'Geçmiş'}
          </button>
          {!readOnly && (
            <button style={{ padding: '3px 9px', fontSize: 12 }}
                    onClick={() => edit((s) => toggleSkip(s, index))}>
              {e.skipped ? 'Geri al' : 'Atla'}
            </button>
          )}
        </div>
      </div>

      {showHistory && (
        <div style={{ marginTop: 10, borderTop: '1px solid var(--grid)', paddingTop: 10 }}>
          <ExerciseHistory slug={e.slug} unit={e.unit} />
        </div>
      )}

      {!e.skipped && (
        <>
          {suggestion.action !== 'NONE' && !readOnly && (
            <div className="suggest">
              <span className="suggest-tag">{ACTION_LABEL[suggestion.action]}</span>
              {suggestion.message}
            </div>
          )}
          <div className="sets" data-loadable={loadable ? "true" : "false"}>
            <div className="sethead">
              <span className="setno" aria-hidden>#</span>
              <span className="prevcol">önceki</span>
              {loadable && <span>kg</span>}
              <span>{e.unit === 'sec' ? 'sn' : 'tekrar'}</span>
              <span />
            </div>
            {e.sets.map((st, k) => {
              const kind: SetKind = st.kind ?? 'normal'
              const before = prevSetText(prev?.log, k)
              return (
              <div className={`setrow${st.done ? ' done' : ''}`} key={k} data-kind={kind}>
                {/* Numara aynı zamanda tip düğmesi: dokununca normal → ısınma →
                    drop → yetmezlik. Ayrı bir menü açmak set arasında pahalı. */}
                <button className="setno" disabled={readOnly}
                        title={SET_KIND_LABEL[kind]} aria-label={SET_KIND_LABEL[kind]}
                        onClick={() => edit((s) => setSetKind(s, index, k, nextKind(kind)))}>
                  {SET_KIND_SHORT[kind] || k + 1}
                </button>

                {/* Geçen seferki aynı set — hedefi hatırlamak için geçmişe
                    gitmeye gerek kalmıyor, karşılaştırma satırın içinde. */}
                <span className="prevcol">{before ?? '—'}</span>

                {loadable && (
                  <input
                    type="number" inputMode="decimal" min={0} step={0.5}
                    disabled={readOnly}
                    placeholder={prev?.log ? String(prev.log.sets.find((x) => x.done)?.weightKg ?? '') : 'kg'}
                    value={st.weightKg ?? ''}
                    onChange={(ev) => {
                      const v = ev.target.value === '' ? null : Math.max(0, Number(ev.target.value))
                      edit((s) => updateSet(s, index, k, { weightKg: v }))
                    }}
                  />
                )}

                <input
                  type="number" inputMode="numeric" min={0}
                  disabled={readOnly}
                  placeholder={`${e.targetLow}–${e.targetHigh}`}
                  value={(e.unit === 'sec' ? st.seconds : st.reps) ?? ''}
                  onChange={(ev) => {
                    const v = ev.target.value === '' ? null : Math.max(0, Number(ev.target.value))
                    edit((s) => updateSet(s, index, k, e.unit === 'sec' ? { seconds: v } : { reps: v }))
                  }}
                />

                <button
                  className={st.done ? 'setdone on' : 'setdone'}
                  disabled={readOnly}
                  aria-label={st.done ? 'Seti geri al' : 'Seti tamamla'}
                  onClick={() => onMarkSet(k)}
                >
                  ✓
                </button>
              </div>
            )})}
          </div>

          {!readOnly && (
            <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
              <button style={{ padding: '2px 9px', fontSize: 12 }}
                      onClick={() => edit((s) => addSet(s, index))}>+ set</button>
              <button style={{ padding: '2px 9px', fontSize: 12 }}
                      disabled={!removeSetGuard(e)}
                      onClick={() => edit((s) => removeSet(s, index))}>− set</button>
            </div>
          )}

          <div className="rpe">
            <span className="rpe-label">Ne kadar zorladı?</span>
            {RPE_SCALE.map((r) => (
              <button
                key={r.value}
                type="button"
                disabled={readOnly}
                title={r.detail}
                aria-pressed={e.rpe === r.value}
                className={e.rpe === r.value ? `rpe-btn on ${verdict}` : 'rpe-btn'}
                onClick={() => edit((s) => setRpe(s, index, e.rpe === r.value ? null : r.value))}
              >
                {r.value}
              </button>
            ))}
          </div>
          {e.rpe !== null && (
            <Prose className="note" style={{ marginTop: 4 }}>
              {RPE_SCALE[e.rpe - 1].detail}
              {verdict === 'low' && ' — bir sonraki sefere yükü artırmak mantıklı.'}
              {verdict === 'high' && ' — bir sonraki sefere aynı yükte kal.'}
            </Prose>
          )}
        </>
      )}
    </div>
  )
}

/** Öneriyi tek kelimeyle etiketle — uzun cümleyi okumadan da yön anlaşılsın. */
const ACTION_LABEL: Record<string, string> = {
  LOAD_UP: 'Yük artır',
  LOAD_DOWN: 'Yük azalt',
  REP_UP: 'Tekrar artır',
  HOLD: 'Aynı kal',
  HOLD_CONFIRM: 'Bir seans daha',
  NONE: '',
}
